import { Breadcrumbs } from '@zoopla-eng/zds-components/breadcrumbs';
import type { BreadcrumbsProps } from '@zoopla-eng/zds-components/breadcrumbs';
import { Box } from '@zoopla-eng/zds-components/box';
import { useState, FC, ReactNode } from 'react';
import { useRouter } from 'next/router';
import { useTheme } from '@emotion/react';
import {
    AdvertProvider,
    AdvertSlotDfp,
    AD_UNIT_ROOTS,
    AD_UNIT_SECTION,
    getPageSectionForAdUnit,
    ADBRIDG_CLASS
} from '@zoopla/adverts';
import type { HeadProps } from '@zoopla/layout/head';
import { Head } from '@zoopla/layout/head';
import triggerTracking from '@blueprint/tracking';
import { Layout, Adverts, Sticky } from './HousePricesLayout.styles';

export const trackBreadcrumbLinkClick = (
    action: string | null,
    value: number
) => {
    triggerTracking({
        event: 'GAEvent',
        eventCategory: 'breadcrumb',
        eventAction: 'clickthrough',
        eventLabel: action || undefined,
        eventValue: value + 1
    });
};

type Props = {
    head: HeadProps;
    breadcrumbs: BreadcrumbsProps['items'];
    children: ReactNode;
};

export const HousePricesLayout: FC<Props> = ({
    head,
    breadcrumbs,
    children
}) => {
    const [advertProviderContext] = useState({
        targeting: { activity: 'house_prices_search' },
        adBridgPage: AD_UNIT_SECTION.HOUSE_PRICES
    });
    const { route, asPath } = useRouter();

    const adUnitPage = getPageSectionForAdUnit(route);

    const theme = useTheme();
    const sidebarBreakpoint = theme.breakpoints[2];

    const breadcrumbsCount = breadcrumbs.length - 1;
    const breadcrumbItems = breadcrumbs.map((bc, idx) => {
        const bci = {
            ...bc,
            onClick: trackBreadcrumbLinkClick
        };

        if (idx === breadcrumbsCount) {
            bci.href = asPath;
        }

        return bci;
    });

    return (
        <AdvertProvider value={advertProviderContext}>
            <Head {...head} />
            <Layout id="main-content">
                <Box
                    {...{
                        padding: '$S200 0',
                        display: 'none',

                        '@M100': {
                            display: 'block'
                        }
                    }}
                >
                    <Breadcrumbs items={breadcrumbItems} />
                </Box>
                <Box display="flex" justifyContent="space-evenly">
                    <Box flexGrow="1">{children}</Box>
                    <Adverts>
                        <AdvertSlotDfp
                            position={0}
                            showOnly={['>', sidebarBreakpoint]}
                            sizes={[[300, 16], 'fluid']}
                            adUnitCode={[
                                AD_UNIT_ROOTS.TEXT_LINK,
                                AD_UNIT_SECTION.HOUSE_PRICES,
                                adUnitPage
                            ]}
                            adbridgClass={ADBRIDG_CLASS.TEXT_LINK}
                        />
                        <AdvertSlotDfp
                            position={1}
                            showOnly={['>', sidebarBreakpoint]}
                            sizes={[[300, 250]]}
                            adUnitCode={[
                                AD_UNIT_ROOTS.MPU,
                                AD_UNIT_SECTION.HOUSE_PRICES,
                                adUnitPage
                            ]}
                            adbridgClass={ADBRIDG_CLASS.MPU}
                        />
                        <Sticky>
                            <AdvertSlotDfp
                                position={2}
                                showOnly={['>', sidebarBreakpoint]}
                                sizes={[[300, 600]]}
                                adUnitCode={[
                                    AD_UNIT_ROOTS.SKY_SCRAPER,
                                    AD_UNIT_SECTION.HOUSE_PRICES,
                                    adUnitPage
                                ]}
                                adbridgClass={ADBRIDG_CLASS.SKYSCRAPER}
                            />
                        </Sticky>
                    </Adverts>
                </Box>
                <Box
                    {...{
                        padding: '$M200 $S400 $S200 $S400',

                        '@M100': {
                            display: 'none'
                        }
                    }}
                >
                    <Breadcrumbs items={breadcrumbItems} />
                </Box>
            </Layout>
        </AdvertProvider>
    );
};

export default HousePricesLayout;
